import { Badge } from "@rosette/ts/src/business/badge/Badge.js";
import type { TagProps } from "@/components/user/Tags.js";


// Equal-priority badges are ordered by their position in Badge.values() (the
// definition order, which also matches the seed/DB-id order). This keeps a
// stable order without threading DB ids through the player payload.
const BADGE_ORDER: Map<string, number> = new Map(
    Badge.values().map((badge, index) => [badge.getID(), index]),
);

function orderIndex(badge: Badge): number {
    return BADGE_ORDER.get(badge.getID()) ?? Number.MAX_SAFE_INTEGER;
}


// Complete class names, so Tailwind sees them.
const TEXT_CLASSES: Record<string, string> = {
    "white": "text-white",
    "on-accent": "text-on-accent",
};
const BACKGROUND_CLASSES: Record<string, string> = {
    "ruleset-finkel": "bg-ruleset-finkel",
    "ruleset-blitz": "bg-ruleset-blitz",
    "ruleset-masters": "bg-ruleset-masters",
};


export function getBadgeColourClasses(badge: Badge): string {
    return `${TEXT_CLASSES[badge.getForegroundColour()]} ${BACKGROUND_CLASSES[badge.getBackgroundColour()]}`;
}


function badgeToTagProps(badge: Badge): TagProps {
    return {
        name: badge.getName(),
        title: badge.getDescription(),
        colourClasses: getBadgeColourClasses(badge),
    };
}


// Resolve badge public_ids to Badge objects (dropping any unknown ones), sorted
// by priority desc then definition order, and collapsed so each family shows
// only its highest-priority (= highest-tier) badge — e.g. a user with both the
// Master and Grandmaster of a format shows only the Grandmaster.
function resolveAndCollapse(publicIDs: string[]): Badge[] {
    const resolved: Badge[] = [];
    for (const publicID of publicIDs) {
        const badge = Badge.getByIDOrNull(publicID);
        if (badge !== null)
            resolved.push(badge);
    }

    resolved.sort((a, b) => {
        const byPriority = b.getPriority() - a.getPriority();
        if (byPriority !== 0)
            return byPriority;
        return orderIndex(a) - orderIndex(b);
    });

    const seenFamilies = new Set<string>();
    const collapsed: Badge[] = [];
    for (const badge of resolved) {
        const family = badge.getFamilyKey();
        if (family !== null) {
            if (seenFamilies.has(family))
                continue;
            seenFamilies.add(family);
        }
        collapsed.push(badge);
    }
    return collapsed;
}


// Profile: every badge the user holds, collapsed and sorted.
export function getProfileBadgeTags(publicIDs: string[]): TagProps[] {
    return resolveAndCollapse(publicIDs).map(badgeToTagProps);
}


// Popup: the top 3 badges, plus any that tie the rank-3 priority (so a full set
// of equal-priority badges — e.g. all three Grandmasters — is never split).
export function getPopupBadgeTags(publicIDs: string[]): TagProps[] {
    const collapsed = resolveAndCollapse(publicIDs);
    if (collapsed.length <= 3)
        return collapsed.map(badgeToTagProps);

    const cutoffPriority = collapsed[2].getPriority();
    const limited: Badge[] = [];
    for (const badge of collapsed) {
        if (limited.length < 3 || badge.getPriority() === cutoffPriority) {
            limited.push(badge);
        } else {
            break;
        }
    }
    return limited.map(badgeToTagProps);
}
